import { IListViewParameter } from '#/CoreFrameInterfaces';
import NavigationHelper from '@/Management/NavigationHelper';
import AssociationNavigationMessage from '@/Messages/AssociationNavigationMessage';
import Messenger from '@/Utility/Messenger';

/**
 * Diese abstrakte Klasse ist Basisklasse für alle konkreten AssociationNavigator-Klassen.
 * Ein AssociationNavigator wird vom System aufgerufen, wenn über eine Assoziation navigiert werden soll.
 * Das anzunavigierende Objekt wird hier als assoziiertes Objekt bezeichnet, das Objekt von dem die Navigation ausgeht, wird assoziierendes Objekt genannt.
 *
 * Verwendet wird die Methode navigateAssociation mit folgenden Parametern:
 *
 *  - typeName: Typ des assoziierten Objekts
 *  - pdoId: ID des assoziierten Objekts
 *  - optionalParams: Weitere Parameter, im Framework bekannt sind mindestens:
 *      - contextSource: null, 'ALV' oder 'SV': Die Art des Kontextes aus dem heraus die Navigation stattfindet
 *      - contextObject: ID des assoziierenden Objekts
 *      - contextTypeName: Typ des assoziierenden Objekts
 *      - contextRelation: Name der Assoziation am assoziierenden Objekt über die navigiert wird
 *
 *      - onNavigationNotPossible: Falls eine Navigation nicht möglich ist, wird dieses Callback aufgerufen
 *      - onNavigated            : Kurz vor der eigentlichen Navigation wird dieses Callback aufgerufen
 * 
 * Konkrete Unterklassen können die Parameter (inkl. optionalParams) nutzen, um die Navigation genauer zu steuern.
 */
export default class AssociationNavigator {
    constructor() {
    }

    public prepareParamsForDetailView(pdoId: string, params) {
        params = $.extend({}, params, { pdoId: pdoId });
        delete params.contextSource;
        return params;
    }

    public prepareParamsForListView(pdoId: string, params: IListViewParameter): IListViewParameter {
        params = $.extend({}, params, { selectedPDO: pdoId });
        delete params.contextSource;
        return params;
    }

    public getListViewNameForType(typeName: string): string {
        return typeName + '_LV';
    }

    public navigateAssociation(typeName: string, pdoId: string, optionalParams: IListViewParameter | any, openInNewWindow: boolean): any {
        throw 'Not yet implemented: navigateToAssociation';
    }

    protected openDetailView(selectedPdoId: string, typeName: string, optionalParams, openInNewWindow: boolean) {
        this.sendAssociationNavigationMessage(typeName, optionalParams);
        const params = this.prepareParamsForDetailView(selectedPdoId, optionalParams);
        NavigationHelper.openDetailView('EDIT', typeName, params, openInNewWindow);
    }

    protected openListView(selectedPDO: string, typeName: string, optionalParams, openInNewWindow: boolean) {
        this.sendAssociationNavigationMessage(typeName, optionalParams);
        const params = this.prepareParamsForListView(selectedPDO, optionalParams);
        NavigationHelper.openListViewByType(typeName, params, openInNewWindow);
    }

    protected sendAssociationNavigationMessage(typeName: string, optionalParams): void {
        if (!optionalParams?.contextRelation)
            return;

        Messenger.send(new AssociationNavigationMessage({
            fromPDTypeName: optionalParams.contextTypeName,
            fromPdoId: optionalParams.contextObject,
            toPDTypeName: typeName,
            toPdoId: optionalParams.pdoId,
            relation: optionalParams.contextRelation,
        }));
    }
}